import React, { useRef } from 'react';
import { IconProps } from '@msc-b2c/interfaces/types';
import clsx from 'clsx';
import { handleKeyDown } from '@msc-b2c/shared-utils/keyboardUtils';
import { getClickCtaMainName, trackEvent } from '@msc-b2c/shared-utils';

export const Icon = React.forwardRef<HTMLElement, IconProps>(function Icon(
  {
    icon,
    ariaHidden = false,
    ariaLabel,
    ariaExpanded,
    onClick,
    onKeyDown,
    styleOption,
    tooltipTitle,
    tooltipAnalytics,
    color,
  },
  ref
) {
  const hasTrackedTooltipInteractionRef = useRef(false);

  const handleKeyDownWrapper = (event: React.KeyboardEvent) => {
    handleKeyDown(event, onClick as (...args: any[]) => void);
  };

  const trackTooltipProxyInteraction = (target: HTMLElement) => {
    if (!tooltipAnalytics || !tooltipTitle || hasTrackedTooltipInteractionRef.current) {
      return;
    }

    trackEvent('click_cta', {
      cta_dest_link: tooltipAnalytics.ctaDestLink || '',
      click_main_name: getClickCtaMainName(target),
      container_info: tooltipAnalytics.containerInfo || '',
      element_position: tooltipAnalytics.elementPosition || 'package card',
      element_type: tooltipAnalytics.elementType || 'tooltip',
      click_location: tooltipAnalytics.clickLocation || 'fixed',
    });

    hasTrackedTooltipInteractionRef.current = true;
  };

  const resetTooltipInteractionTracking = () => {
    hasTrackedTooltipInteractionRef.current = false;
  };

  return (
    icon !== 'no-icon' && (
      <i
        ref={ref}
        aria-hidden={ariaHidden}
        aria-label={ariaHidden ? undefined : ariaLabel}
        {...(ariaExpanded !== undefined && { 'aria-expanded': ariaExpanded })}
        className={clsx(
          'icon',
          { [`icon-${icon}`]: icon },
          { 'clickable-icon': icon && onClick },
          styleOption
        )}
        onClick={onClick}
        onMouseEnter={(event) => trackTooltipProxyInteraction(event.currentTarget)}
        onFocus={(event) => trackTooltipProxyInteraction(event.currentTarget)}
        onMouseLeave={resetTooltipInteractionTracking}
        onBlur={resetTooltipInteractionTracking}
        role={onClick && !ariaHidden ? 'button' : undefined}
        tabIndex={onClick && !ariaHidden ? 0 : -1}
        onKeyDown={onKeyDown || (onClick ? handleKeyDownWrapper : undefined)}
        data-testid={`icon - ${ariaLabel || 'default'}`}
        title={tooltipTitle ?? ''}
        style={{ color }}
      ></i>
    )
  );
});
